import type { JobPayrollChildTableRowVM, JobPayrollTableRowVM } from 'ab-viewModels/accounting/jobPayrollTable.viewModel';

import { filterMap } from '@acceligentllc/shared/utils/array';
import * as TimeUtils from '@acceligentllc/shared/utils/time';

import { TimeFormat } from '@acceligentllc/shared/enums/timeFormat';

type ComplexValueGetter = (parentRow: JobPayrollTableRowVM, childRow?: JobPayrollChildTableRowVM) => string;

export type JobPayrollTableCSVComplexValueGetterLookup = Record<ComplexValueCSVKeys, ComplexValueGetter>;

export const toCSVData = (
	jobRows: JobPayrollTableRowVM[],
	complexValueGetters: JobPayrollTableCSVComplexValueGetterLookup,
	showTotalColumns = false,
	showCertCols = false,
	showWaiverCol = false
): string[][] => {
	let rows: string[][] = [CSV_HEADER_ROW];

	if (!showTotalColumns) {
		rows = [rows[0].filter((value) => !BETA_COLUMN_LABELS.includes(value))];
	} else if (!showCertCols) {
		rows = [rows[0].filter((value) => !CERT_COLUMN_LABELS.includes(value))];
	} else if (!showWaiverCol) {
		rows = [rows[0].filter((value) => !WAIVER_COLUMN_LABELS.includes(value))];
	}

	for (const _parentRow of jobRows) {
		for (const _childRow of _parentRow.childTable.rows ?? []) {
			const _currentRow: string[] = [];

			for (const _metadata of CSV_METADATA_LIST) {
				if (!showTotalColumns && BETA_COLUMN_LABELS.includes(_metadata.label)) {
					continue;
				}
				if (!showCertCols && CERT_COLUMN_LABELS.includes(_metadata.label)) {
					continue;
				}
				if (!showWaiverCol && WAIVER_COLUMN_LABELS.includes(_metadata.label)) {
					continue;
				}
				_currentRow.push(_toCSVCell(_metadata, _parentRow, _childRow, complexValueGetters));
			}
			rows.push(_currentRow);
		}
	}
	return rows;
};

const _toCSVCell = (
	metadata: CSVMetadata,
	parentRow: JobPayrollTableRowVM,
	childRow: JobPayrollChildTableRowVM,
	complexValueGetters: JobPayrollTableCSVComplexValueGetterLookup
): string => {
	if (metadata.isComplexValue) {
		return complexValueGetters[metadata.key](parentRow, childRow);
	}
	const simpleValueMetadata = metadata as SimpleValueCSVMetadata;

	const input = simpleValueMetadata.isInChildTable ? childRow[simpleValueMetadata.key] : parentRow[simpleValueMetadata.key as ParentRowCSVKeys];
	const formatter: Nullable<(value: typeof input) => string> = 'formatter' in simpleValueMetadata ? simpleValueMetadata.formatter ?? null : null; // required to infer typing

	// eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing
	return (formatter ? formatter(input) : `${input || ''}`) || simpleValueMetadata.fallback || '';
};

// #region Private:CSV

const _booleanFormatter = (value: boolean) => value ? 'Yes' : 'No';
const _dateOnlyFormatter = (value: string) => TimeUtils.formatDate(value, TimeFormat.DATE_ONLY, TimeFormat.DB_DATE_ONLY);

type ParentRowCSVKeys = keyof JobPayrollTableRowVM & (
	| 'calculatedJobCode'
	| 'jobTitle'
	| 'divisionName'
	| 'allCrewIsInternal'
);
type ChildRowCSVKeys = keyof JobPayrollChildTableRowVM & (
	| 'localDateValue'
	| 'workOrderShift'
	| 'workOrderWeeklyCode'
	| 'wosPerAccount'
	| 'isWorkOrderCanceled'
	| 'crewName'
	| 'userUniqueId'
	| 'userFullName'
	| 'accountLocationAddressState'
	| 'isTimeSheetSigned'
	| 'isTimeSheetApproved'
	| 'timeSheetNote'
	| 'fieldWorkClassificationCode'
	| 'equipmentIdCodes'
	| 'jobHours'
	| 'totalJobHours'
	| 'breakHours'
	| 'totalBreakHours'
	| 'shopHours'
	| 'totalShopHours'
	| 'travelHours'
	| 'totalTravelHours'
	| 'totalHours'
	| 'totalHoursPerWO'
	| 'certStatus'
	| 'certReason'
	| 'missedBreakCount'
	| 'waiverStatus'
);

/** Complex keys require dynamic getters/formatters to be created in runtime */
type ComplexValueCSVKeys = 'reportUrl';

type CSVKey = ParentRowCSVKeys | ChildRowCSVKeys | ComplexValueCSVKeys;

interface SharedCSVMetadataProps {
	label: string;
	index: number;
}
interface ParentRowCSVMetadataProps<TKey extends ParentRowCSVKeys> extends SharedCSVMetadataProps {
	isInChildTable: false;
	formatter?: (value: JobPayrollTableRowVM[TKey]) => string;
	fallback?: string;
	isComplexValue?: false;
}
interface ChildRowCSVMetadataProps<TKey extends ChildRowCSVKeys> extends SharedCSVMetadataProps {
	isInChildTable: true;
	formatter?: (value: JobPayrollChildTableRowVM[TKey]) => string;
	fallback?: string;
	isComplexValue?: false;
}
interface ComplexValueCSVMetadataProps extends SharedCSVMetadataProps {
	isComplexValue: true;
}

/** Value used ONLY inside `CSV_METADATA_LOOKUP` to initialize `index` values */
let CSV_METADATA_LOOKUP_INITIALIZE_LAST_INDEX = -1;

const CSV_METADATA_LOOKUP: Readonly<(
	& { [ParentKey in ParentRowCSVKeys]: Readonly<ParentRowCSVMetadataProps<ParentKey>>; }
	& { [ChildKey in ChildRowCSVKeys]: Readonly<ChildRowCSVMetadataProps<ChildKey>>; }
	& { [ComplexKey in ComplexValueCSVKeys]: Readonly<ComplexValueCSVMetadataProps> }
)> = {
	localDateValue: { isInChildTable: true, label: 'Date', index: ++CSV_METADATA_LOOKUP_INITIALIZE_LAST_INDEX, formatter: _dateOnlyFormatter },
	calculatedJobCode: { isInChildTable: false, label: 'Job Id', index: ++CSV_METADATA_LOOKUP_INITIALIZE_LAST_INDEX },
	workOrderShift: { isInChildTable: true, label: 'Work Order', index: ++CSV_METADATA_LOOKUP_INITIALIZE_LAST_INDEX },
	workOrderWeeklyCode: { isInChildTable: true, label: 'Work Order Weekly', index: ++CSV_METADATA_LOOKUP_INITIALIZE_LAST_INDEX },
	wosPerAccount: { isInChildTable: true, label: '# WOs', index: ++CSV_METADATA_LOOKUP_INITIALIZE_LAST_INDEX },
	userUniqueId: { isInChildTable: true, label: 'Employee ID', index: ++CSV_METADATA_LOOKUP_INITIALIZE_LAST_INDEX },
	userFullName: { isInChildTable: true, label: 'Employee Name', index: ++CSV_METADATA_LOOKUP_INITIALIZE_LAST_INDEX },
	divisionName: { isInChildTable: false, label: 'Division', index: ++CSV_METADATA_LOOKUP_INITIALIZE_LAST_INDEX },
	accountLocationAddressState: { isInChildTable: true, label: 'Home State', index: ++CSV_METADATA_LOOKUP_INITIALIZE_LAST_INDEX },
	fieldWorkClassificationCode: { isInChildTable: true, label: 'Classification Code', index: ++CSV_METADATA_LOOKUP_INITIALIZE_LAST_INDEX, fallback: '[MISSING]' },
	equipmentIdCodes: { isInChildTable: true, label: 'Equipment', index: ++CSV_METADATA_LOOKUP_INITIALIZE_LAST_INDEX },
	timeSheetNote: { isInChildTable: true, label: 'Time Card Note', index: ++CSV_METADATA_LOOKUP_INITIALIZE_LAST_INDEX },
	jobHours: { isInChildTable: true, label: 'Job Hours', index: ++CSV_METADATA_LOOKUP_INITIALIZE_LAST_INDEX },
	shopHours: { isInChildTable: true, label: 'Shop Hours', index: ++CSV_METADATA_LOOKUP_INITIALIZE_LAST_INDEX },
	travelHours: { isInChildTable: true, label: 'Travel Hours', index: ++CSV_METADATA_LOOKUP_INITIALIZE_LAST_INDEX },
	breakHours: { isInChildTable: true, label: 'Break Hours', index: ++CSV_METADATA_LOOKUP_INITIALIZE_LAST_INDEX },
	totalJobHours: { isInChildTable: true, label: 'Total Job Hours', index: ++CSV_METADATA_LOOKUP_INITIALIZE_LAST_INDEX },
	totalShopHours: { isInChildTable: true, label: 'Total Shop Hours', index: ++CSV_METADATA_LOOKUP_INITIALIZE_LAST_INDEX },
	totalTravelHours: { isInChildTable: true, label: 'Total Travel Hours', index: ++CSV_METADATA_LOOKUP_INITIALIZE_LAST_INDEX },
	totalBreakHours: { isInChildTable: true, label: 'Total Break Hours', index: ++CSV_METADATA_LOOKUP_INITIALIZE_LAST_INDEX },
	totalHours: { isInChildTable: true, label: 'Total Hours', index: ++CSV_METADATA_LOOKUP_INITIALIZE_LAST_INDEX },
	totalHoursPerWO: { isInChildTable: true, label: 'Total Hours/WO', index: ++CSV_METADATA_LOOKUP_INITIALIZE_LAST_INDEX },
	jobTitle: { isInChildTable: false, label: 'Job Title', index: ++CSV_METADATA_LOOKUP_INITIALIZE_LAST_INDEX },
	allCrewIsInternal: { isInChildTable: false, label: 'Job Is Internal', index: ++CSV_METADATA_LOOKUP_INITIALIZE_LAST_INDEX, formatter: _booleanFormatter },
	crewName: { isInChildTable: true, label: 'Crew', index: ++CSV_METADATA_LOOKUP_INITIALIZE_LAST_INDEX },
	isTimeSheetSigned: { isInChildTable: true, label: 'Time Is Signed', index: ++CSV_METADATA_LOOKUP_INITIALIZE_LAST_INDEX, formatter: _booleanFormatter },
	isTimeSheetApproved: { isInChildTable: true, label: 'Time Is Approved', index: ++CSV_METADATA_LOOKUP_INITIALIZE_LAST_INDEX, formatter: _booleanFormatter },
	isWorkOrderCanceled: { isInChildTable: true, label: 'Work Order Is Cancelled', index: ++CSV_METADATA_LOOKUP_INITIALIZE_LAST_INDEX, formatter: _booleanFormatter },
	certStatus: { isInChildTable: true, label: 'Attestation Cert. Response', index: ++CSV_METADATA_LOOKUP_INITIALIZE_LAST_INDEX },
	certReason: { isInChildTable: true, label: 'Attestation Cert. Reason', index: ++CSV_METADATA_LOOKUP_INITIALIZE_LAST_INDEX },
	missedBreakCount: { isInChildTable: true, label: 'MMBP', index: ++CSV_METADATA_LOOKUP_INITIALIZE_LAST_INDEX },
	waiverStatus: { isInChildTable: true, label: 'Waiver status', index: ++CSV_METADATA_LOOKUP_INITIALIZE_LAST_INDEX },
	reportUrl: { isComplexValue: true, label: 'Report URL', index: ++CSV_METADATA_LOOKUP_INITIALIZE_LAST_INDEX },
};

type _CSVMetadataLookup = { [TKey in CSVKey]: (typeof CSV_METADATA_LOOKUP)[TKey] & { key: TKey; } };
type CSVMetadata = _CSVMetadataLookup[CSVKey];
type SimpleValueCSVMetadata = Omit<_CSVMetadataLookup, ComplexValueCSVKeys>[Exclude<CSVKey, ComplexValueCSVKeys>];

const CSV_METADATA_LIST = Object.keys(CSV_METADATA_LOOKUP)
	.map((_key): CSVMetadata => ({ ...CSV_METADATA_LOOKUP[_key], key: _key }))
	.sort((_e1, _e2) => _e1.index - _e2.index);

const CSV_HEADER_ROW = CSV_METADATA_LIST.map((_metadata) => _metadata.label);

const BETA_COLUMN_KEYS = [
	'totalJobHours',
	'totalBreakHours',
	'totalShopHours',
	'totalTravelHours',
	'totalHours',
	'totalHoursPerWO',
];

const CERT_COLUM_KEYS = [
	'certStatus',
	'certReason',
	'missedBreakCount',
];

const WAIVER_COLUMN_KEYS = [
	'waiverStatus',
];

const BETA_COLUMN_LABELS = filterMap(CSV_METADATA_LIST, (value) => BETA_COLUMN_KEYS.includes(value.key), (value) => value.label);
const CERT_COLUMN_LABELS = filterMap(CSV_METADATA_LIST, (value) => CERT_COLUM_KEYS.includes(value.key), (value) => value.label);
const WAIVER_COLUMN_LABELS = filterMap(CSV_METADATA_LIST, (value) => WAIVER_COLUMN_KEYS.includes(value.key), (value) => value.label);
